import { Link, useParams, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { useSpaces } from '@/hooks/queries/useSpaces';
import { useObject } from '@/hooks/queries/useObjects';
import { cn } from '@/lib/utils';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

export function Breadcrumbs() {
  const location = useLocation();
  const params = useParams<{ spaceKey?: string; objectId?: string }>();
  const { data: spacesData } = useSpaces();
  const currentSpace = spacesData?.data?.find((s) => s.key === params.spaceKey);
  const { data: objectData } = useObject(currentSpace?.id, params.objectId);

  const items: BreadcrumbItem[] = [{ label: 'Hjem', href: '/' }];

  // Build breadcrumb based on current route
  if (location.pathname.startsWith('/spaces')) {
    items.push({ label: 'Arbeidsområder', href: '/spaces' });

    if (params.spaceKey && currentSpace) {
      items.push({
        label: currentSpace.name,
        href: `/spaces/${params.spaceKey}`,
      });

      if (params.objectId && objectData) {
        items.push({ label: objectData.name });
      }
    }
  } else if (location.pathname.startsWith('/search')) {
    items.push({ label: 'Søkeresultater' });
  }

  if (items.length <= 1) {
    return null;
  }

  return (
    <nav aria-label="Brødsmuler" className="flex items-center text-sm text-muted-foreground">
      {items.map((item, index) => {
        const isLast = index === items.length - 1;

        return (
          <div key={index} className="flex items-center">
            {index > 0 && <ChevronRight className="mx-2 h-4 w-4" />}
            {item.href && !isLast ? (
              <Link
                to={item.href}
                className={cn(
                  'hover:text-foreground transition-colors',
                  index === 0 && 'flex items-center gap-1',
                )}
              >
                {index === 0 && <Home className="h-4 w-4" />}
                <span className={index === 0 ? 'sr-only' : ''}>{item.label}</span>
              </Link>
            ) : (
              <span className={cn('font-medium', isLast && 'text-foreground')}>{item.label}</span>
            )}
          </div>
        );
      })}
    </nav>
  );
}
